import { camelCase } from 'lodash-es';
import type {
  CardCountInfo,
  StatusCardGroupInfo,
  StatusCardInfo,
  StatusScope,
} from './statusCard.types';
import { ALERT_ICON_MAP, STATUS_TITLE_MAP, COLOR_MAP } from './statusCard.define';

const statusCountMapperFn = <Status extends string>(
  key: Status,
  value: number,
): StatusCardInfo<Status> => ({
  label: STATUS_TITLE_MAP[key],
  value,
  status: key,
  colorType: COLOR_MAP[key],
});

const alertCountMapperFn = <Alert extends string>(
  key: Alert,
  value: number,
): StatusCardInfo<Alert> => ({
  ...statusCountMapperFn<Alert>(key, value),
  icon: ALERT_ICON_MAP[key],
});

const getInitialCounts = <T extends string>(array: T[]): Record<T, number> =>
  Object.fromEntries(array.map((field) => [field, 0])) as Record<T, number>;

export const statusCardInfoGetter = <
  T,
  Alert extends string = never,
  Status extends string = never,
>(
  data: T[],
  options: {
    statusKey?: keyof T;
    alertKey?: keyof T;
    statusFields?: Status[];
    alertFields?: Alert[];
    fieldMapper?: Record<string, string>;
  },
): StatusCardGroupInfo<Alert, Status> => {
  const { statusKey, alertKey, statusFields = [], alertFields = [], fieldMapper } = options ?? {};
  const initialCountInfo: CardCountInfo<Alert, Status> = {
    total: 0,
    alert: getInitialCounts<Alert>(alertFields),
    status: getInitialCounts<Status>(statusFields),
  };

  const hasOtherBucket = statusFields.includes('other' as Status);
  const knownStatusFields = statusFields.filter((f) => f !== 'other');

  const countInfo = data.reduce<CardCountInfo<Alert, Status>>((acc, cur) => {
    if (alertKey) {
      const alertLowerCase = camelCase((cur[alertKey] as string)?.toLowerCase());

      if (alertLowerCase) {
        if (!acc.alert[alertLowerCase]) {
          acc.alert[alertLowerCase] = 0;
        }
        acc.alert[alertLowerCase] += 1;
      }
    }
    if (statusKey) {
      const statusLowerCase = camelCase((cur[statusKey] as string)?.toLowerCase());
      const statusField = fieldMapper?.[statusLowerCase] ?? statusLowerCase;

      if (statusField) {
        const resolvedField =
          hasOtherBucket && !knownStatusFields.includes(statusField as Status)
            ? 'other'
            : statusField;

        if (!acc.status[resolvedField]) {
          acc.status[resolvedField] = 0;
        }
        acc.status[resolvedField] += 1;
      }
    }

    acc.total += 1;
    return acc;
  }, initialCountInfo);

  const alertCardInfo = (Object.entries(countInfo.alert) as [Alert, number][]).map<
    StatusCardInfo<Alert>
  >(([key, value]) => alertCountMapperFn<Alert>(key as Alert, value));
  const statusCardInfo = (Object.entries(countInfo.status) as [Status, number][]).map<
    StatusCardInfo<Status>
  >(([key, value]) => statusCountMapperFn<Status>(key as Status, value));

  return {
    alert: alertCardInfo,
    status: statusCardInfo,
    total: countInfo.total,
  };
};

export const statusFilterCallback = (
  selectedStatus: string,
  currentAlert?: string,
  currentStatus?: string,
  knownStatuses?: string[],
  selectedScope?: StatusScope,
) => {
  if (selectedStatus === 'total') {
    return true;
  }

  if (selectedStatus === 'other' && knownStatuses) {
    const normalizedStatus = camelCase(currentStatus?.toLowerCase());
    return !knownStatuses.includes(normalizedStatus);
  }

  const statusMatch = selectedStatus === camelCase(currentStatus?.toLowerCase());
  const alertMatch = selectedStatus === camelCase(currentAlert?.toLowerCase());

  if (selectedScope === 'status') return statusMatch;
  if (selectedScope === 'alert') return alertMatch;
  return statusMatch || alertMatch;
};
